import React, { useMemo, useRef, useState } from 'react';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Box,
  HStack,
  VStack,
  Text,
  Badge,
  Button,
  Divider,
  Spinner,
  useMediaQuery,
  useToast,
  useDisclosure,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import {
  TrendingUp,
  TrendingDown,
  Mountain,
  Gauge,
  Clock,
  Route as RouteIcon,
  Download,
  ArrowRight,
  Wind,
  Droplets,
  Smartphone,
  Square,
} from 'lucide-react';
import { toPng } from 'html-to-image';
import ElevationChart from './ElevationChart';
import ShareCard from './ShareCard';
import WeatherIcon from './WeatherIcon';
import {
  formatDistance,
  formatDuration,
  formatElevation,
  formatGrade,
  formatHHMM,
  climbCategoryLabel,
  segmentArrivalTimes,
} from './format';
import {
  getForecastAtPassingTime,
  formatPassingTime,
} from '../../../../util/passingTimes';

function StatCard({ icon, label, value, color }) {
  return (
    <Box
      w="48%"
      h="120px"
      bgColor="#d9d9d926"
      borderRadius={8}
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      overflow="hidden"
      p={2}
    >
      <HStack spacing={1} color="gray.500" mb={1}>
        {icon}
        <Text textStyle="weatherCardHeading" textTransform="uppercase" fontSize="11px">
          {label}
        </Text>
      </HStack>
      <Text textStyle="weatherCardData" lineHeight={1} color={color || 'gray.800'}>
        {value}
      </Text>
    </Box>
  );
}

// Format picker shown when the user taps "Descargar imagen".
function FormatPickerModal({ isOpen, onClose, onPick, isExporting }) {
  const { t } = useTranslation();
  const formats = [
    {
      id: 'square',
      label: t('map.profile.formatSquare'),
      desc: t('map.profile.formatSquareDesc'),
      icon: <Square size={18} />,
      previewW: 80,
      previewH: 80,
    },
    {
      id: 'story',
      label: t('map.profile.formatStory'),
      desc: t('map.profile.formatStoryDesc'),
      icon: <Smartphone size={18} />,
      previewW: 45,
      previewH: 80,
    },
  ];
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="xs">
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent bg="white" color="gray.800" borderRadius={12} mx={4}>
        <ModalCloseButton />
        <ModalBody pt={6} pb={6}>
          <Text fontWeight={600} fontSize="md" mb={4} textAlign="center">
            {t('map.profile.formatPickerTitle')}
          </Text>
          <HStack spacing={4} justify="center">
            {formats.map((f) => (
              <Button
                key={f.id}
                variant="unstyled"
                onClick={() => { onClose(); onPick(f.id); }}
                isDisabled={!!isExporting}
                display="flex"
                flexDirection="column"
                alignItems="center"
                h="auto"
                p={0}
                _hover={{ opacity: 0.8 }}
              >
                {/* Visual proportion thumbnail */}
                <Box
                  w="100px"
                  h="120px"
                  borderRadius={8}
                  bg="gray.100"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  mb={2}
                  border="1px solid"
                  borderColor="gray.200"
                  _hover={{ borderColor: '#38A169' }}
                  transition="border-color 0.15s"
                >
                  <Box
                    w={`${f.previewW}px`}
                    h={`${f.previewH}px`}
                    borderRadius={4}
                    bg="gray.300"
                    border="2px solid #38A169"
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                    p={1}
                    overflow="hidden"
                  >
                    {/* Mini elevation line */}
                    <svg width="100%" height="30" viewBox="0 0 100 30" preserveAspectRatio="none">
                      <polyline
                        points="0,28 15,20 30,15 45,18 60,8 75,12 90,5 100,10"
                        fill="none"
                        stroke="#38A169"
                        strokeWidth="2"
                      />
                    </svg>
                    <Box w="100%" h="1px" bg="blackAlpha.200" mt={1} />
                    <HStack spacing={0} justify="space-between" mt={1}>
                      {[0,1,2].map(i => (
                        <Box key={i} w="30%" h="3px" bg="blackAlpha.200" borderRadius={1} />
                      ))}
                    </HStack>
                  </Box>
                </Box>
                <HStack spacing={1} mb={1}>
                  {f.icon}
                  <Text fontSize="sm" fontWeight={600}>{f.label}</Text>
                </HStack>
              </Button>
            ))}
          </HStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export default function RouteProfile(props) {
  const {
    isOpen,
    onClose,
    profile,
    isLoading,
    localities,
    origin,
    destination,
    departureTime,
    travelMode,
  } = props;
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.split('-')[0] || 'es';
  const toast = useToast();
  const [isLargerThanMobile] = useMediaQuery('(min-width: 768px)');
  const shareSquareRef = useRef(null);
  const shareStoryRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const {
    isOpen: isFormatPickerOpen,
    onOpen: onFormatPickerOpen,
    onClose: onFormatPickerClose,
  } = useDisclosure();

  const stats = profile?.stats || null;
  const samples = profile?.samples || [];

  const segments = useMemo(
    () => segmentArrivalTimes(profile?.segments || [], departureTime),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [profile?.segments, departureTime],
  );

  // Build weather summary row per locality. Falls back to empty if no forecast yet.
  const weatherRows = useMemo(() => {
    if (!Array.isArray(localities) || localities.length === 0) return [];
    return localities
      .map((loc) => {
        const hour = getForecastAtPassingTime(loc.weatherForecast, loc.passingTime);
        return {
          name: loc.localityName,
          passingTime: loc.passingTime,
          weatherCode: hour?.weatherCode ?? null,
          period: hour?.period ?? null,
          temperature: hour?.temperature ?? null,
          windSpeed: hour?.windSpeed ?? null,
          precipitation: hour?.precipitation ?? null,
          hasData: !!hour,
        };
      })
      .filter((r) => r.name); // always show localities even if forecast is missing
  }, [localities]);

  const handleDownload = async (format) => {
    const ref = format === 'story' ? shareStoryRef : shareSquareRef;
    if (!ref.current) return;
    setExporting(format);
    try {
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      const dataUrl = await toPng(ref.current, { cacheBust: true });
      const slug = (origin || 'ruta').toLowerCase().replace(/\s+/g, '-');
      const fileName = `routeforecast-${slug}-${format}.png`;

      try {
        const blob = await (await fetch(dataUrl)).blob();
        const file = new File([blob], fileName, { type: 'image/png' });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: 'RouteForecast',
            text: `${origin} - ${destination}`,
          });
          setExporting(false);
          return;
        }
      } catch (e) {
        // fall through to direct download link
      }

      const link = document.createElement('a');
      link.download = fileName;
      link.href = dataUrl;
      link.click();
    } catch (e) {
      toast({
        title: t('map.profile.exportError'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setExporting(false);
    }
  };

  const drawerBg = 'white';

  return (
    <>
      <Drawer
        isOpen={isOpen}
        placement={isLargerThanMobile ? 'right' : 'bottom'}
        onClose={onClose}
        size={isLargerThanMobile ? 'md' : 'full'}
      >
        <DrawerOverlay />
        <DrawerContent
          bg={drawerBg}
          color="gray.800"
          maxH={isLargerThanMobile ? '100dvh' : '90dvh'}
          borderTopRadius={isLargerThanMobile ? 0 : 16}
        >
          <DrawerCloseButton />
          <DrawerHeader pb={2}>
            <HStack spacing={2}>
              <RouteIcon size={18} color="#38A169" />
              <Text fontSize="lg" fontWeight={600}>
                {t('map.profile.title')}
              </Text>
            </HStack>
            <Text fontSize="sm" color="gray.400" fontWeight={400} mt={1} noOfLines={1}>
              {origin}
              <ArrowRight size={11} style={{ display: 'inline', margin: '0 4px' }} />
              {destination}
            </Text>
          </DrawerHeader>

          <DrawerBody pb={8} overflowY="auto">
            {isLoading ? (
              <VStack py={12} spacing={4}>
                <Spinner color="#38A169" />
                <Text fontSize="sm" color="gray.500">
                  {t('map.profile.loading')}
                </Text>
              </VStack>
            ) : (
              <VStack align="stretch" spacing={5}>
                {/* Elevation chart */}
                <ElevationChart samples={samples} climbs={stats?.climbs} dark />

                {/* Stats grid */}
                <HStack w="100%" flexWrap="wrap" gap={2} justifyContent="space-between">
                  <StatCard
                    icon={<RouteIcon size={12} />}
                    label={t('map.profile.distance')}
                    value={formatDistance(profile?.totalDistance)}
                  />
                  <StatCard
                    icon={<Clock size={12} />}
                    label={t('map.profile.duration')}
                    value={formatDuration(profile?.totalDurationSec)}
                  />
                  <StatCard
                    icon={<TrendingUp size={12} />}
                    label={t('map.profile.ascent')}
                    value={stats ? formatElevation(stats.ascent) : '--'}
                    color="green.400"
                  />
                  <StatCard
                    icon={<TrendingDown size={12} />}
                    label={t('map.profile.descent')}
                    value={stats ? formatElevation(stats.descent) : '--'}
                    color="red.300"
                  />
                  <StatCard
                    icon={<Mountain size={12} />}
                    label={t('map.profile.maxEle')}
                    value={stats ? formatElevation(stats.maxEle) : '--'}
                  />
                  <StatCard
                    icon={<Gauge size={12} />}
                    label={t('map.profile.maxGrade')}
                    value={stats ? formatGrade(stats.maxGrade) : '--'}
                  />
                </HStack>

                {/* Climbs */}
                {stats?.climbs?.length > 0 && (
                  <Box>
                    <Text textStyle="weatherCardHeading" textTransform="uppercase" mb={2} color="gray.500">
                      {t('map.profile.climbsTitle')}
                    </Text>
                    <VStack align="stretch" spacing={2}>
                      {stats.climbs.map((c, i) => (
                        <HStack key={i} justify="space-between" bgColor="#d9d9d926" borderRadius={8} p={3}>
                          <HStack spacing={3}>
                            <Badge
                              colorScheme="green"
                              borderRadius="full"
                              px={2}
                              py={1}
                              fontSize="11px"
                              fontWeight={700}
                            >
                              {climbCategoryLabel(c.category, t)}
                            </Badge>
                            <Text fontSize="sm">{formatDistance(c.length)}</Text>
                          </HStack>
                          <HStack spacing={4}>
                            <Text fontSize="sm" color="green.400" fontWeight={500}>
                              +{c.gain} m
                            </Text>
                            <Text fontSize="xs" color="gray.400">
                              {formatGrade(c.avgGrade)} {t('map.profile.avg')}
                            </Text>
                          </HStack>
                        </HStack>
                      ))}
                    </VStack>
                  </Box>
                )}

                <Divider borderColor="gray.200" />

                {/* Segment breakdown */}
                {segments.length > 0 && (
                  <Box>
                    <Text textStyle="weatherCardHeading" textTransform="uppercase" mb={2} color="gray.500">
                      {t('map.profile.segmentsTitle')}
                    </Text>
                    <VStack align="stretch" spacing={0}>
                      {segments.map((seg, i) => {
                        const delta =
                          seg.toEle != null && seg.fromEle != null
                            ? Math.round(seg.toEle - seg.fromEle)
                            : null;
                        return (
                          <HStack
                            key={i}
                            justify="space-between"
                            py={2}
                            borderBottomWidth={i < segments.length - 1 ? '1px' : 0}
                            borderColor="gray.200"
                          >
                            <VStack align="start" spacing={0} flex={1} minW={0}>
                              <Text fontSize="sm" noOfLines={1}>
                                {seg.from}
                                <ArrowRight size={10} style={{ display: 'inline', margin: '0 3px' }} />
                                {seg.to}
                              </Text>
                              <HStack spacing={2} color="gray.400">
                                <Text fontSize="xs">{formatDistance(seg.distance)}</Text>
                                <Text fontSize="xs">·</Text>
                                <Text fontSize="xs">{formatHHMM(seg.arrivalTime)}</Text>
                              </HStack>
                            </VStack>
                            {delta != null && (
                              <HStack
                                spacing={1}
                                color={delta >= 0 ? 'green.400' : 'red.300'}
                                minW="60px"
                                justify="flex-end"
                              >
                                {delta >= 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
                                <Text fontSize="sm">
                                  {delta >= 0 ? '+' : ''}{delta} m
                                </Text>
                              </HStack>
                            )}
                          </HStack>
                        );
                      })}
                    </VStack>
                  </Box>
                )}

                {/* Weather along the route */}
                {weatherRows.length > 0 && (
                  <Box>
                    <Text textStyle="weatherCardHeading" textTransform="uppercase" mb={2} color="gray.500">
                      {t('map.profile.weatherTitle')}
                    </Text>
                    <VStack align="stretch" spacing={0}>
                      {weatherRows.map((row, i) => (
                        <HStack
                          key={i}
                          justify="space-between"
                          py={2}
                          borderBottomWidth={i < weatherRows.length - 1 ? '1px' : 0}
                          borderColor="gray.200"
                        >
                          <HStack spacing={2} flex={1} minW={0}>
                            <WeatherIcon
                              weatherCode={row.weatherCode}
                              period={row.period}
                              size={28}
                            />
                            <VStack align="start" spacing={0} minW={0}>
                              <Text fontSize="sm" noOfLines={1}>
                                {row.name}
                              </Text>
                              <Text fontSize="xs" color="gray.400">
                                {formatPassingTime(row.passingTime)}
                              </Text>
                            </VStack>
                          </HStack>
                          <HStack spacing={3} color="gray.700">
                            {row.temperature != null && (
                              <Text fontSize="sm" fontWeight={600}>
                                {row.temperature}°
                              </Text>
                            )}
                            {row.windSpeed != null && (
                              <HStack spacing={1} color="gray.400">
                                <Wind size={12} />
                                <Text fontSize="xs">{row.windSpeed} km/h</Text>
                              </HStack>
                            )}
                            {row.precipitation != null && row.precipitation > 0 && (
                              <HStack spacing={1} color="blue.300">
                                <Droplets size={12} />
                                <Text fontSize="xs">{row.precipitation} mm</Text>
                              </HStack>
                            )}
                            {!row.hasData && (
                              <Text fontSize="xs" color="gray.600">—</Text>
                            )}
                          </HStack>
                        </HStack>
                      ))}
                    </VStack>
                  </Box>
                )}

                {/* Download */}
                <Divider borderColor="gray.200" />
                <Button
                  leftIcon={<Download size={16} />}
                  colorScheme="green"
                  isLoading={!!exporting}
                  loadingText={t('map.profile.generating')}
                  onClick={onFormatPickerOpen}
                  isDisabled={!!exporting}
                  w="100%"
                >
                  {t('map.profile.download')}
                </Button>
              </VStack>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Format picker modal */}
      <FormatPickerModal
        isOpen={isFormatPickerOpen}
        onClose={onFormatPickerClose}
        onPick={handleDownload}
        isExporting={exporting}
      />

      {/* Off-screen share cards. Transform on WRAPPER, not on the ref element,
          so html-to-image captures the card without transform inlined. */}
      {isOpen && (
        <>
          <div
            aria-hidden="true"
            style={{ position: 'fixed', top: 0, left: 0, transform: 'translateX(-9999px)', pointerEvents: 'none', zIndex: -1 }}
          >
            <ShareCard
              ref={shareSquareRef}
              format="square"
              profile={profile}
              segments={segments}
              origin={origin}
              destination={destination}
              departureTime={departureTime}
              travelMode={travelMode}
              lang={lang}
              weatherRows={weatherRows}
            />
          </div>
          <div
            aria-hidden="true"
            style={{ position: 'fixed', top: 0, left: 0, transform: 'translateX(-9999px)', pointerEvents: 'none', zIndex: -1 }}
          >
            <ShareCard
              ref={shareStoryRef}
              format="story"
              profile={profile}
              segments={segments}
              origin={origin}
              destination={destination}
              departureTime={departureTime}
              travelMode={travelMode}
              lang={lang}
              weatherRows={weatherRows}
            />
          </div>
        </>
      )}
    </>
  );
}
